/* eslint-disable no-empty */
/* eslint-disable @typescript-eslint/no-empty-function */
/* eslint-disable no-console */
import { stackExtractor } from '~/utilities/newyu';

const _console = window.console;
const _leadingText = {
  DEBUG: 'DEBUG:',
  ERROR: 'ERROR:',
  INFO: 'INFO:',
  PANIC: 'PANIC!!:',
  WARN: 'WARN:',
};

let level = 'DEBUG';
let _log = () => {};
let _debug = () => {};
let _groupAndTrace = () => {};
let _info = () => {};
let _warn = () => {};
let _error = () => {};

// We execute our logger functions from within a try/catch as IE throws execeptions
// when trying to access console's native functions from other references
const tryLog = function (
  _logger: (...message: unknown[]) => void,
  ...msg: unknown[]
) {
  try {
    // The following check looks to see if we are printing one of our `_leadingText` options, followed by a string
    // If we are, then we want to combine those two strings into one parameter so that text substitution still works as intended
    // This is because if the first parameter contains substitution characters, then the following parameters will be injected in
    // Per the web standard: console.log(msg [, subst1, ..., substN]);
    if (
      msg.length > 2 &&
      typeof msg[0] === 'string' &&
      typeof msg[1] === 'string' &&
      Object.values(_leadingText).includes(msg[0])
    ) {
      const [msg1, msg2, ...rest] = msg;

      _logger(`${msg1} ${msg2}`, ...rest);
    } else {
      _logger(...msg);
    }
  } catch {}
};

const log = (...msg: string[]) => {
  tryLog(_log, ...msg);
};

export const debug = (...msg: string[]) => {
  tryLog(_debug, _leadingText.DEBUG, ...msg);
};

/**
 * This will minimally print out a message that is a grouped message.
 * The first argument you pass in is going to be uses at the top level of the group.
 * That message will always be seen and is bold.
 * All additional arguments will be individually printed (via debug log) inside of the group structure.
 * If an additional argument is an array, it will be spread into the debug log.
 * For example, if an argument is `['result', resultObject]` that will be spread as two individual
 * arguments to the debug log method.
 * The final message within the group is a trace which will print out the trace (this works in dev
 * mode despite the webpacking)
 * The group is collapsed initially to keep the console log window a bit cleaner.
 * @param {...any} msg arguments for the log statement
 */
export const groupAndTrace = (...msg: unknown[]) => {
  tryLog(_groupAndTrace, ...msg);
};

/**
 * @public
 */
export const info = (...msg: unknown[]) => {
  tryLog(_info, _leadingText.INFO, ...msg);
};

const warn = (...msg: unknown[]) => {
  tryLog(_warn, _leadingText.WARN, ...msg);
};

export const error = (...msg: unknown[]) => {
  tryLog(_error, _leadingText.ERROR, ...msg);
};

export const panic = function (...msg: string[]) {
  if (level !== 'OFF') {
    tryLog(
      _error,
      _leadingText.PANIC,
      ...msg,
      stackExtractor(new Error(...msg)),
    );
    throw new Error(...msg);
  }
};

export const init = (_level: string) => {
  level = _level || level;

  if (level !== 'OFF') {
    /* eslint no-fallthrough: "off" */ // Fallthrough for logging levels
    switch (level) {
      case 'DEBUG':
        _debug = _console.log;
        _log = _console.log;
        _groupAndTrace = (...msg: string[]) => {
          const title = msg[0] ?? undefined;

          console.groupCollapsed(`DEBUG-TRACE ${title}`);

          if (title) {
            msg.slice(1, msg.length).forEach((m) => {
              if (Array.isArray(m)) {
                m.forEach((message) => {
                  log(message as string);
                });
              } else {
                log(m);
              }
            });
          }

          console.trace('trace');
          console.groupEnd();
        };

        console.debug = debug;
        console.log = log;
        break;
      case 'INFO':
        _info = _console.info;
        console.info = info;
        break;
      case 'WARN':
        _warn = _console.warn;
        console.warn = warn;
        break;
      case 'ERROR':
        _error = _console.error;
        console.error = error;
    }

    log(`Logger initialized to: ${level}`);
  }
};

/**
 * Returns the current log level
 */
export const getLogLevel = () => level;

const propertiesFilter = ['password'];

const replacer = <T>(key: string, val: T) => {
  if (propertiesFilter.includes(key)) {
    return '';
  }

  return val;
};

/**
 * Will return a string version of the object with sensitive properties removed
 * If the object is circular then it will just return null
 * @param {*} obj
 */
export const cleanObjectPropertiesToString = (obj: unknown): string | null => {
  try {
    return JSON.stringify(obj, replacer, 2);
  } catch {
    return null;
  }
};

/**
 * Will return a new object with sensitive properties removed
 * If the object is circular, it won't clean it and will just return the original
 * object
 * @param {*} obj
 */
export const cleanObjectProperties = (obj: unknown): unknown => {
  const stringed = cleanObjectPropertiesToString(obj);

  if (stringed === null) {
    return obj;
  }

  return JSON.parse(stringed);
};

export default {
  debug,
  error,
  getLogLevel,
  groupAndTrace,
  info,
  log,
  panic,
  warn,
};
